import { html, property, state } from 'lit-element';
import { KatLitElement, register } from '../../shared/base';
import { debounce } from '../../utils/debounce';
import baseStyles from '../../shared/base/base.lit.scss';
import styles from './activity-list.lit.scss';

const enum ComponentWidth {
  Small = 375,
  Medium = 720,
  Large = 1024,
  XLarge = 1200,
  XXLarge = 1201, // Anything greater than XLarge is XXLarge.
}

interface ComponentWidthAttribute {
  cssClass: string;
  cardsPerSize: number;
}

/**
 * There is a tight-coupling between cardsPerSize and width of a card in
 * ./activity-list.lit.scss. If these values change, be sure to also update
 * the flex-basis of a card.
 */
const componentWidthAttributes: Record<
  ComponentWidth,
  ComponentWidthAttribute
> = {
  [ComponentWidth.Small]: {
    cssClass: 'small',
    cardsPerSize: 1, // technically 1.1, but pages & scrolling should behave as if it's 1
  },
  [ComponentWidth.Medium]: {
    cssClass: 'medium',
    cardsPerSize: 1, // technically 1.3, but pages & scrolling should behave as if it's 1
  },
  [ComponentWidth.Large]: {
    cssClass: 'large',
    cardsPerSize: 2,
  },
  [ComponentWidth.XLarge]: {
    cssClass: 'xlarge',
    cardsPerSize: 3,
  },
  [ComponentWidth.XXLarge]: {
    cssClass: 'xxlarge',
    cardsPerSize: 4,
  },
};

/**
 * @component {kat-activity-list} KatActivityList The Activity List component standardizes the display of a activities the user might wish to complete in a horizontal carousel. These activities could be (and aren’t limited to) work-in-progress tasks to be continued or recommended actions.
 * @subcomponent ./activity-list-item.ts
 * @status Production
 * @example Default {
 *    "header": "In-Progress Shipments",
 *    "subheader": "Last updated: Today at 11:07AM",
 *    "content": "
 *      <kat-activity-list-item header='Task 1' timestamp='Last updated: 3 minutes ago'>
 *        <kat-button slot='primary-action' variant='tertiary'>
 *          Resume
 *        </kat-button>
 *        <kat-dropdown-button single-target slot='secondary-actions' options='[{\"label\":\"Edit\",\"action\":\"edit\"}]'></kat-dropdown-button>
 *      </kat-activity-list-item>
 *      <kat-activity-list-item header='Task 2' timestamp='Last updated: 3 minutes ago'>
 *        <kat-button slot='primary-action' variant='tertiary'>
 *          Resume
 *        </kat-button>
 *        <kat-dropdown-button single-target slot='secondary-actions' options='[{\"label\":\"Edit\",\"action\":\"edit\"}]'></kat-dropdown-button>
 *      </kat-activity-list-item>
 *      <kat-activity-list-item header='Task 3' timestamp='Last updated: 3 minutes ago'>
 *        <kat-button slot='primary-action' variant='tertiary'>
 *          Resume
 *        </kat-button>
 *        <kat-dropdown-button single-target slot='secondary-actions' options='[{\"label\":\"Edit\",\"action\":\"edit\"}]'></kat-dropdown-button>
 *      </kat-activity-list-item>
 *      <kat-activity-list-item header='Task 4' timestamp='Last updated: 3 minutes ago'>
 *        <kat-button slot='primary-action' variant='tertiary'>
 *          Resume
 *        </kat-button>
 *        <kat-dropdown-button single-target slot='secondary-actions' options='[{\"label\":\"Edit\",\"action\":\"edit\"}]'></kat-dropdown-button>
 *      </kat-activity-list-item>
 *    "}
 * @a11y {keyboard}
 * @a11y {sr}
 * @a11y {contrast}
 */
@register('kat-activity-list')
export class KatActivityList extends KatLitElement {
  static get styles() {
    return [baseStyles, styles];
  }

  constructor() {
    super();

    this._listeners = [
      {
        target: window,
        listeners: [['resize', () => this.handleWindowResize()]],
      },
    ];
  }

  /**
   * The title of the module, summarizing the content it contains.
   */
  @property()
  header: string;

  /**
   * A secondary text area for secondary or clarifying information,
   * such as a "last updated" timestamp.
   */
  @property()
  subheader: string;

  @state()
  currentPage = 0;

  @state()
  cardsPerPage = 6;

  @state()
  totalPages = 1;

  @state()
  maxPageSegments = 1;

  firstUpdated() {
    this.handleWindowResize();
    this.setupScrollObserver();
  }

  updated(changedProperties) {
    if (changedProperties.has('cardsPerPage')) {
      this.calcTotalPages();
    }

    if (changedProperties.has('currentPage')) {
      this.shadowRoot.querySelectorAll('.kat-nav-dot').forEach(dot => {
        dot.classList.toggle(
          'current',
          Number(dot.getAttribute('data-index')) === this.currentPage
        );
      });
    }
  }

  private getActivityCards = () =>
    Array.from(this.querySelectorAll('kat-activity-list-item'));

  private handleWindowResize() {
    this.calcCardsPerPage();
    this.calcMaxPageSegments();
  }

  @debounce(100)
  private handleCardScroll() {
    this.updateButtonState();
  }

  private handleSlotChange = () => {
    // add new pages if necessary
    this.calcTotalPages();
    // re-enable nav buttons if necessary
    this.updateButtonState();
    // setup intersection observer on new cards
    this.setupScrollObserver();
  };

  private setupScrollObserver = () => {
    const visibleCards = new Set();
    // eslint-disable-next-line compat/compat
    const intersectionObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            visibleCards.add(entry.target);
          } else {
            visibleCards.delete(entry.target);
          }

          // Non-visible cards should not be interacted with
          entry.target.toggleAttribute('inert', !entry.isIntersecting);

          // If every card on a page is visible, set that page to the current page
          const cardsOnPageOfEntry = this.getCardsOnPage(
            this.getPageOfCard(entry.target)
          );
          if (cardsOnPageOfEntry.every(card => visibleCards.has(card))) {
            this.currentPage = this.getPageOfCard(entry.target);
          }
        });
      },
      {
        root: this.shadowRoot.querySelector('.task-bar'),
        /**
         * 70% of a card must be intersecting to be "visible".
         * This value correlates with "medium" sized cards.
         */
        threshold: 0.7,
      }
    );

    this.getActivityCards().forEach(card => {
      // prevent duplicate observers
      intersectionObserver.unobserve(card);
      intersectionObserver.observe(card);
    });
  };

  private updateButtonState = () => {
    const leftButton = this.shadowRoot.querySelector('button.left');
    const rightButton = this.shadowRoot.querySelector('button.right');

    const cardBar = this.shadowRoot.querySelector('.tasks');
    const isNotScrolled = cardBar.scrollLeft <= 0;
    const isMaxScrolled =
      cardBar.scrollWidth - cardBar.scrollLeft <= cardBar.clientWidth;

    if (isNotScrolled) {
      leftButton.setAttribute('disabled', '');
    } else {
      leftButton.removeAttribute('disabled');
    }

    if (isMaxScrolled) {
      rightButton.setAttribute('disabled', '');
    } else {
      rightButton.removeAttribute('disabled');
    }
  };

  private getPageOfCard = (card: Element): number =>
    Math.floor(this.getActivityCards().indexOf(card) / this.cardsPerPage);

  private calcCardsPerPage = () => {
    const listWidth = this.getBoundingClientRect().width;

    if (
      listWidth <= ComponentWidth.XLarge &&
      listWidth > ComponentWidth.Large
    ) {
      this.setCardsPerPageAttributes(ComponentWidth.XLarge);
    } else if (
      listWidth <= ComponentWidth.Large &&
      listWidth > ComponentWidth.Medium
    ) {
      this.setCardsPerPageAttributes(ComponentWidth.Large);
    } else if (
      listWidth <= ComponentWidth.Medium &&
      listWidth > ComponentWidth.Small
    ) {
      this.setCardsPerPageAttributes(ComponentWidth.Medium);
    } else if (listWidth <= ComponentWidth.Small) {
      this.setCardsPerPageAttributes(ComponentWidth.Small);
    } else {
      this.setCardsPerPageAttributes(ComponentWidth.XXLarge);
    }
  };

  private calcMaxPageSegments = () => {
    const listWidth = this._shadow('.task-bar').getBoundingClientRect().width;
    this.maxPageSegments = Math.floor(listWidth / 32);
  };

  private calcTotalPages = () => {
    this.totalPages = Math.ceil(
      this.getActivityCards().length / this.cardsPerPage
    );
  };

  private setCardsPerPageAttributes = (width: ComponentWidth) => {
    const allCardSizeCSSClasses = Object.values(componentWidthAttributes).map(
      attr => attr.cssClass
    );

    const taskbar = this.shadowRoot.querySelector('.task-bar');

    taskbar.classList.remove(...allCardSizeCSSClasses);
    taskbar.classList.add(componentWidthAttributes[width].cssClass);

    this.cardsPerPage = componentWidthAttributes[width].cardsPerSize;
  };

  private handleLeftClick = () => {
    this.goToPage(this.currentPage - 1);
  };

  private handleRightClick = () => {
    this.goToPage(this.currentPage + 1);
  };

  private handleDotClick = (index: number) => {
    this.goToPage(index);
  };

  private renderDot = (index: number) =>
    html`
      <button
        class="kat-nav-dot"
        data-index=${index}
        part="nav-dot-${index}"
        @click=${() => this.handleDotClick(index)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="8"
          height="8"
          viewBox="0 0 8 8"
          fill="none"
        >
          <rect width="8" height="8" rx="4" />
        </svg>
      </button>
    `;

  private renderDots = () => {
    const MAX_PAGE_SEGMENTS = this.maxPageSegments;
    const MAX_DIFF_FROM_ENDPOINTS = MAX_PAGE_SEGMENTS - 1;
    // The math below is easier using 1-based indexing
    const currentPage = this.currentPage + 1;

    const dots = [];

    const generateDots = (
      startPageInclusive: number,
      endPageExclusive: number
    ) => {
      for (let i = startPageInclusive; i < endPageExclusive; i++) {
        dots.push(this.renderDot(i));
      }
    };

    const generateEllipsis = () => {
      dots.push(html`
        <kat-icon name="more_horiz" size="small" class="ellipsis"></kat-icon>
      `);
    };

    let hasLeftEllipsis = false;
    let hasRightEllipsis = false;

    const leftThreshold = Math.min(
      this.totalPages - MAX_DIFF_FROM_ENDPOINTS,
      MAX_DIFF_FROM_ENDPOINTS - 1
    );
    const rightThreshold = this.totalPages - MAX_DIFF_FROM_ENDPOINTS;
    if (this.totalPages > MAX_PAGE_SEGMENTS) {
      hasLeftEllipsis = currentPage > leftThreshold;
      hasRightEllipsis = currentPage <= rightThreshold;
    }

    if (!hasLeftEllipsis && !hasRightEllipsis) {
      generateDots(0, this.totalPages);
    } else if (hasLeftEllipsis && !hasRightEllipsis) {
      generateEllipsis();
      generateDots(this.totalPages - MAX_DIFF_FROM_ENDPOINTS, this.totalPages);
    } else if (!hasLeftEllipsis && hasRightEllipsis) {
      generateDots(0, MAX_DIFF_FROM_ENDPOINTS);
      generateEllipsis();
    } else {
      const leftDelta = Math.floor(MAX_DIFF_FROM_ENDPOINTS / 2) - 1;
      const rightDelta = Math.ceil(MAX_DIFF_FROM_ENDPOINTS / 2);

      generateEllipsis();
      generateDots(this.currentPage - leftDelta, this.currentPage + rightDelta);
      generateEllipsis();
    }

    return dots;
  };

  private getCardsOnPage = (page: number) =>
    this.getActivityCards().filter(
      (card, index) =>
        index >= Math.floor(page * this.cardsPerPage) &&
        index < Math.floor((page + 1) * this.cardsPerPage)
    );

  /**
   * Transitions to the indicated page (zero-based indexing). Pages below or above the limit will transition
   * to the first or last page, respectively.
   * @param {number} newPage The page to transition to.
   * @katalmethod
   */
  public goToPage(newPage: number) {
    const shouldScrollToCenter = this.cardsPerPage === 1;

    const scrollOptions: ScrollIntoViewOptions = {
      behavior: 'smooth',
      block: 'nearest',
      inline: shouldScrollToCenter ? 'center' : 'nearest',
    };

    if (newPage >= this.totalPages - 1) {
      const cardbar = this._shadow('.tasks');
      cardbar.scrollTo({ left: cardbar.scrollWidth, behavior: 'smooth' });

      return;
    }

    if (newPage <= 0) {
      this._shadow('.tasks').scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }

    if (newPage < this.currentPage) {
      this.getCardsOnPage(newPage)[0].scrollIntoView(scrollOptions);
    } else {
      const cardsOnPage = this.getCardsOnPage(newPage);

      cardsOnPage[cardsOnPage.length - 1].scrollIntoView(scrollOptions);
    }
  }

  render() {
    return html`
      <div class="container">
        <h2 class="header">${this.header}</h2>
        <span class="subheader">${this.subheader}</span>
        <div class="task-bar">
          <button
            class="left"
            type="button"
            part="left-nav-button"
            @click=${this.handleLeftClick}
          >
            <kat-icon name="chevron-left" size="small"></kat-icon>
          </button>
          <div class="tasks" @scroll=${this.handleCardScroll}>
            <slot @slotchange=${this.handleSlotChange}></slot>
          </div>
          <button
            class="right"
            type="button"
            part="right-nav-button"
            @click=${this.handleRightClick}
          >
            <kat-icon name="chevron-right" size="small"></kat-icon>
          </button>
        </div>
        <div class="task-pages">${this.renderDots()}</div>
      </div>
    `;
  }
}
